import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../api/cliente.js';
import { ZONA_POR_DEFECTO } from '../lib/formato.js';
import { useDiasConTurnos, useSlots } from '../lib/hooks.js';

import { Cabecera } from '../componentes/Cabecera.jsx';
import { Mapa } from '../componentes/Mapa.jsx';
import { Progreso } from '../componentes/Progreso.jsx';
import { Pie } from '../componentes/Pie.jsx';
import { Estado } from '../componentes/Estado.jsx';
import { ListaCargando } from '../componentes/Cargando.jsx';

import { PasoServicio } from '../pasos/PasoServicio.jsx';
import { PasoProfesional } from '../pasos/PasoProfesional.jsx';
import { PasoDia } from '../pasos/PasoDia.jsx';
import { PasoHorario } from '../pasos/PasoHorario.jsx';
import { PasoPago } from '../pasos/PasoPago.jsx';
import { PasoDatos } from '../pasos/PasoDatos.jsx';
import { Confirmacion } from '../pasos/Confirmacion.jsx';
import { BarraDemo } from '../componentes/BarraDemo.jsx';
import { esDemo } from '../lib/demo.js';
import { vocabularioDe } from '../lib/vocabulario.js';
import { variablesDeMarca } from '../lib/marca.js';

/**
 * Los títulos de cada paso, con la palabra del rubro puesta.
 *
 * El único que cambia es el de lo que se reserva: «¿Con quién?» en una
 * barbería y «¿Qué cancha?» en un club. Las palabras vienen del servidor, con
 * el negocio: ver services/vocabulario.js.
 */
const titulos = (v) => ({
  servicio: {
    titulo: v.familia === 'cancha' ? '¿Cuánto tiempo?' : '¿Qué te querés hacer?',
    bajada: v.familia === 'cancha' ? 'Elegí la duración del turno.' : 'Elegí el servicio.'
  },
  profesional: { titulo: v.preguntaPublica, bajada: v.bajadaPublica },
  dia: { titulo: '¿Qué día?', bajada: 'Los días en gris no tienen turnos libres.' },
  horario: { titulo: '¿A qué hora?', bajada: null },
  pago: {
    titulo: '¿Cómo querés pagar?',
    bajada: 'Elegí una y seguimos con tus datos.'
  },
  datos: { titulo: 'Tus datos', bajada: 'Nombre, teléfono y email. Nada más.' }
});

/**
 * Lo que este servicio deja pagar, con los montos ya resueltos por el servidor.
 *
 * El `?? {}` es esta pantalla hablándole a un servidor viejo: durante un
 * despliegue el orden no está garantizado, y un servicio sin `cobro` tiene que
 * comportarse como antes —se reserva y se paga en el local— y no romperse.
 */
function opcionesDePago(servicio) {
  const cobro = servicio?.cobro ?? {};
  return {
    local: cobro.local !== false,
    senia_centavos: Number(cobro.senia_centavos) || 0,
    total_centavos: Number(cobro.total_centavos) || 0
  };
}

/** ¿Hay algo que preguntar? Con una sola opción no se pregunta nada. */
const hayQueElegirPago = (opciones) =>
  [opciones.local, opciones.senia_centavos > 0, opciones.total_centavos > 0]
    .filter(Boolean).length > 1;

/**
 * La página pública de reservas.
 *
 * La demo se envuelve en vez de tocar las cinco ramas de ReservaEnCurso: la
 * barra tiene que salir en los cinco pasos Y en la confirmación, y agregarla
 * rama por rama es garantizar que el día que alguien sume una sexta se olvide.
 * El div de afuera además deja apagar la cabecera pegajosa: son dos elementos
 * pegados arriba y se taparían entre sí.
 */
export function Reserva() {
  const { slug } = useParams();
  if (!esDemo(slug)) return <ReservaEnCurso />;

  return (
    <div className="demo-marco">
      <BarraDemo />
      <ReservaEnCurso demo />
    </div>
  );
}

function ReservaEnCurso({ demo = false }) {
  const { slug } = useParams();

  const [negocio, setNegocio] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [cargaInicial, setCargaInicial] = useState({ cargando: true, error: null });

  const [profesionales, setProfesionales] = useState([]);
  const [cargaProfesionales, setCargaProfesionales] = useState({ cargando: false, error: null });

  const [seleccion, setSeleccion] = useState({
    servicio: null, profesional: null, fecha: null, slot: null, pago: null
  });
  const [paso, setPaso] = useState('servicio');
  const [yendoAPagar, setYendoAPagar] = useState(false);
  const [saltearProfesional, setSaltearProfesional] = useState(false);

  const [enviando, setEnviando] = useState(false);
  const [avisoOcupado, setAvisoOcupado] = useState(null);
  const [reserva, setReserva] = useState(null);
  const [reintentoInicial, setReintentoInicial] = useState(0);

  const encabezado = useRef(null);
  const zona = negocio?.timezone || ZONA_POR_DEFECTO;

  // ─── Carga inicial: el negocio y sus servicios ──────────────────────────
  useEffect(() => {
    let vigente = true;
    setCargaInicial({ cargando: true, error: null });

    Promise.all([api.negocio(slug), api.servicios(slug)])
      .then(([datosNegocio, datosServicios]) => {
        if (!vigente) return;
        setNegocio(datosNegocio);
        setServicios(datosServicios);
        setCargaInicial({ cargando: false, error: null });
      })
      .catch((fallo) => {
        if (vigente) setCargaInicial({ cargando: false, error: fallo });
      });

    return () => { vigente = false; };
  }, [slug, reintentoInicial]);

  // El título de la pestaña lleva el nombre del negocio: es lo que se ve al
  // compartir el link.
  useEffect(() => {
    if (!negocio) return;
    document.title = `Reservar turno en ${negocio.nombre}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', `Reservá tu turno en ${negocio.nombre} en menos de un minuto.`);
  }, [negocio]);

  // ─── El color del negocio ────────────────────────────────────────────────
  //
  // Se aplica sobre el <html> y no como style de cada <div className="pantalla">
  // porque esos divs son cinco —cargando, no toma reservas, error, confirmado y
  // el flujo— y el día que alguien agregue el sexto se olvidaría del color.
  // Mismo criterio que el título de la pestaña, acá arriba: un efecto que toca
  // el documento y lo deja como estaba al salir.
  //
  // El cleanup importa: desde esta página se puede navegar al panel, y el panel
  // es verde siempre. Para el negocio que no eligió color, variablesDeMarca
  // devuelve null y acá no pasa nada.
  useEffect(() => {
    const variables = variablesDeMarca(negocio?.color_marca);
    if (!variables) return undefined;

    const raiz = document.documentElement;
    for (const [nombre, valor] of Object.entries(variables)) {
      raiz.style.setProperty(nombre, valor);
    }
    return () => {
      for (const nombre of Object.keys(variables)) raiz.style.removeProperty(nombre);
    };
  }, [negocio?.color_marca]);

  // Al cambiar de paso el foco va al encabezado: quien navega con teclado o
  // con lector de pantalla se entera de que la pantalla cambió.
  useEffect(() => { encabezado.current?.focus(); }, [paso]);

  const { dias, cargando: cargandoDias, error: errorDias, reintentar: reintentarDias } =
    useDiasConTurnos({
      slug,
      profesional: seleccion.profesional,
      servicio: seleccion.servicio,
      timezone: zona
    });

  const { slots, cargando: cargandoSlots, error: errorSlots, recargar: recargarSlots } =
    useSlots({
      slug,
      profesional: seleccion.profesional,
      servicio: seleccion.servicio,
      fecha: seleccion.fecha
    });

  // ─── Navegación ─────────────────────────────────────────────────────────
  // Los pasos se arman según el negocio: con un solo profesional ese paso no
  // existe y la barra tiene cuatro tramos, no cinco. Mostrar un paso que
  // nunca se va a ver haría que el progreso parezca trabado.
  //
  // El paso del pago aparece sólo si hay más de una forma de pagar. Con una
  // sola no hay nada que elegir: se usa ésa y la persona no toca una pantalla
  // de más para contestar algo que ya estaba decidido.
  const opcionesPago = opcionesDePago(seleccion.servicio);
  const preguntarPago = hayQueElegirPago(opcionesPago);

  const pasos = [
    'servicio',
    ...(saltearProfesional ? [] : ['profesional']),
    'dia',
    'horario',
    ...(preguntarPago ? ['pago'] : []),
    'datos'
  ];

  const indice = Math.max(pasos.indexOf(paso), 0);

  // Lo que se va a cobrar, ya decidido. Cuando no hay nada que preguntar sale
  // la única opción que existe: pagar en el local si se puede, y si no, la
  // seña, y si tampoco, el total. El monto nunca se calcula acá: los dos
  // números vienen del servidor con el servicio.
  const modoPago = preguntarPago
    ? seleccion.pago
    : (opcionesPago.local ? 'sin_pago' : (opcionesPago.senia_centavos > 0 ? 'senia' : 'total'));

  const centavosAPagar = modoPago === 'senia'
    ? opcionesPago.senia_centavos
    : (modoPago === 'total' ? opcionesPago.total_centavos : 0);

  // Lo que ve el resumen de la última pantalla, con la política del negocio.
  const seniaElegida = centavosAPagar > 0
    ? { centavos: centavosAPagar, modo: modoPago, politica: negocio?.senia_politica }
    : null;

  async function elegirServicio(servicio) {
    // Cambiar de servicio invalida todo lo que venía después: otro servicio
    // dura distinto y puede hacerlo otra gente.
    // Cambiar de servicio también borra la forma de pago: otro servicio puede
    // no ofrecer la que había elegido.
    setSeleccion({ servicio, profesional: null, fecha: null, slot: null, pago: null });
    setCargaProfesionales({ cargando: true, error: null });
    setPaso('profesional');

    try {
      const equipo = await api.profesionales(slug, servicio.id);
      setProfesionales(equipo);
      setCargaProfesionales({ cargando: false, error: null });

      // Con un solo profesional no tiene sentido preguntar: se elige solo.
      if (equipo.length === 1) {
        setSaltearProfesional(true);
        setSeleccion((previa) => ({ ...previa, profesional: equipo[0] }));
        setPaso('dia');
      } else {
        setSaltearProfesional(false);
      }
    } catch (fallo) {
      setCargaProfesionales({ cargando: false, error: fallo.message });
    }
  }

  function elegirProfesional(profesional) {
    setSeleccion((previa) => ({ ...previa, profesional, fecha: null, slot: null }));
    setPaso('dia');
  }

  // Marcar el primer día libre no avanza de pantalla: la persona sigue
  // viendo el calendario y decide.
  const preseleccionarDia = useCallback((fecha) => {
    setSeleccion((previa) => ({ ...previa, fecha }));
  }, []);

  function elegirDia(fecha) {
    setSeleccion((previa) => ({ ...previa, fecha, slot: null }));
    setPaso('horario');
  }

  function elegirSlot(slot) {
    setSeleccion((previa) => ({ ...previa, slot }));
    setAvisoOcupado(null);
    setPaso(preguntarPago ? 'pago' : 'datos');
  }

  function elegirPago(modo) {
    setSeleccion((previa) => ({ ...previa, pago: modo }));
    setPaso('datos');
  }

  function volver() {
    const anterior = pasos[indice - 1];
    if (anterior) setPaso(anterior);
  }

  function empezarDeNuevo() {
    setSeleccion({ servicio: null, profesional: null, fecha: null, slot: null, pago: null });
    setSaltearProfesional(false);
    setReserva(null);
    setAvisoOcupado(null);
    setPaso('servicio');
  }

  // ─── Reserva ────────────────────────────────────────────────────────────
  async function reservar({ nombre, telefono, email }) {
    setEnviando(true);
    setAvisoOcupado(null);

    try {
      const respuesta = await api.reservar(slug, {
        nombre,
        telefono,
        email,
        servicio_id: seleccion.servicio.id,
        profesional_id: seleccion.profesional.id,
        inicio: seleccion.slot.inicio,
        // Cuál de las opciones eligió, no cuánto sale: el monto lo resuelve el
        // servidor con el servicio que de verdad está en la base. Si por algún
        // camino raro no hay elección, no se manda el campo y decide el
        // servidor, que es lo mismo que hacía la versión anterior.
        ...(modoPago ? { pago_modo: modoPago } : {})
      });

      // El email se guarda con la reserva para poder mostrarlo en la pantalla
      // de confirmación: "te mandamos un email a ana@..." vale mucho más que
      // "te mandamos un email", sobre todo si se equivocó al escribirlo.
      // ─── Con seña, la reserva todavía no terminó ────────────────────
      //
      // El turno quedó guardado pero en 'pendiente', y lo que lo confirma es
      // la plata. Así que no se muestra ninguna pantalla de "listo": se la
      // manda a pagar y se vuelve a /{slug}/pago, que es donde se le dice
      // cómo salió. Mostrarle un "turno confirmado" acá sería mentirle, y la
      // mentira se descubre sola quince minutos después.
      if (respuesta.pago?.url) {
        // El boton queda deshabilitado hasta que el navegador se vaya: si se
        // reactivara, un segundo click crearia un segundo turno sobre el
        // mismo horario mientras la pagina todavia esta en pantalla.
        setYendoAPagar(true);
        window.location.assign(respuesta.pago.url);
        return;
      }

      setReserva({
        ...respuesta,
        duracionMinutos: seleccion.servicio.duracion_minutos,
        email
      });
      setPaso('confirmado');
    } catch (fallo) {
      // El caso que de verdad importa: alguien se quedó con el horario
      // mientras esta persona escribía su nombre. Se vuelve a los horarios
      // con la grilla recargada y el motivo a la vista.
      //
      // Mandarla de vuelta a los horarios tiene sentido sólo si el horario se
      // ocupó. Cuando lo que falló fue abrir el pago —mismo 409, otro código—
      // el horario sigue libre y el turno ya se soltó: se queda donde está,
      // con sus datos escritos, y vuelve a intentar.
      if (fallo.tipo === 'ocupado' && fallo.codigo !== 'cobro_no_disponible') {
        setSeleccion((previa) => ({ ...previa, slot: null }));
        setAvisoOcupado(fallo.message);
        recargarSlots();
        setPaso('horario');
      } else {
        setAvisoOcupado(fallo.message);
      }
    } finally {
      setEnviando(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  if (cargaInicial.cargando) {
    return (
      <div className="pantalla">
        <Cabecera negocio={null} />
        <main className="contenido">
          <div className="esqueleto" style={{ height: 28, width: '60%', marginBottom: 24 }} />
          <ListaCargando filas={3} />
        </main>
      </div>
    );
  }

  // ─── El negocio no está tomando reservas ────────────────────────────────
  //
  // Se muestra ANTES del formulario y en vez de él: dejar elegir servicio,
  // profesional y horario para rebotar al final es hacerle perder un minuto a
  // alguien por algo que ya sabíamos en la primera request.
  //
  // El texto NUNCA dice por qué. Lo que pasó es entre el negocio y nosotros;
  // el cliente final no tiene por qué enterarse de que a su peluquero se le
  // venció la prueba, y menos de que no pagó. Por eso tampoco se ofrece
  // "probá más tarde": lo honesto es mandarlo a escribirle al negocio.
  if (negocio && negocio.acepta_reservas === false) {
    const wa = negocio.whatsapp
      ? `https://wa.me/${negocio.whatsapp}?text=${encodeURIComponent(`Hola ${negocio.nombre}! Quería sacar un turno.`)}`
      : null;

    return (
      <div className="pantalla">
        <Cabecera negocio={negocio} />
        <main className="contenido">
          <Estado icono="calendario" titulo="No está tomando reservas">
            <p>
              {negocio.nombre} no está recibiendo reservas por acá en este
              momento.
            </p>
            {wa && (
              <p className="estado-extra">
                Escribile por WhatsApp para sacar tu turno.
              </p>
            )}
          </Estado>
          {wa && (
            <a className="boton boton-whatsapp-publico" href={wa} target="_blank" rel="noreferrer">
              Escribir por WhatsApp
            </a>
          )}
        </main>
        <Pie />
      </div>
    );
  }

  if (cargaInicial.error) {
    const noExiste = cargaInicial.error.tipo === 'no_encontrado';

    return (
      <div className="pantalla">
        <Cabecera negocio={null} />
        <main className="contenido">
          <Estado
            icono={noExiste ? 'buscar' : 'sinSenal'}
            titulo={noExiste ? 'No encontramos este negocio' : 'No pudimos cargar la página'}
            accion={noExiste ? undefined : () => setReintentoInicial((n) => n + 1)}
            etiquetaAccion="Probar de nuevo"
          >
            <p>
              {noExiste
                ? 'Puede que el link esté mal escrito o que el negocio ya no use HolaTurnos.'
                : cargaInicial.error.message}
            </p>
          </Estado>
        </main>
        <Pie />
      </div>
    );
  }

  if (paso === 'confirmado') {
    return (
      <div className="pantalla">
        <Cabecera negocio={negocio} />
        {/* Las mismas dos columnas que el flujo, y no el mapa abajo del todo:
            es el momento en que "¿dónde queda?" pasa a ser la única pregunta
            que queda, así que menos que nunca conviene esconderlo. */}
        <main className="contenido con-columna">
          <div className="columna-flujo">
            <Confirmacion
              reserva={reserva}
              negocio={negocio}
              fecha={seleccion.fecha}
              timezone={zona}
              onNuevoTurno={empezarDeNuevo}
              demo={demo}
            />
          </div>

          <Mapa direccion={negocio?.direccion} nombre={negocio?.nombre} />
        </main>
        <Pie />
      </div>
    );
  }

  const { titulo, bajada } = titulos(vocabularioDe(negocio))[paso];

  return (
    <div className="pantalla">
      <Cabecera negocio={negocio} />

      {/* `con-columna` convierte el contenido en dos columnas de 900px para
          arriba, con el mapa al costado. Abajo de eso el div de la izquierda
          es todo el ancho y el mapa no se dibuja: en el celular es el botón
          «Ver mapa» de la cabecera el que lo muestra. */}
      <main className="contenido con-columna">
        <div className="columna-flujo">
          <Progreso
            paso={indice + 1}
            total={pasos.length}
            onVolver={volver}
            puedeVolver={indice > 0}
          />

          <h1 className="titulo-paso" tabIndex={-1} ref={encabezado}>{titulo}</h1>
          {bajada && <p className="bajada">{bajada}</p>}

          {paso === 'servicio' && (
            <PasoServicio
              servicios={servicios}
              cargando={false}
              error={null}
              elegido={seleccion.servicio}
              onElegir={elegirServicio}
              onReintentar={() => setReintentoInicial((n) => n + 1)}
            />
          )}

          {paso === 'profesional' && (
            <PasoProfesional
              profesionales={profesionales}
              cargando={cargaProfesionales.cargando}
              error={cargaProfesionales.error}
              elegido={seleccion.profesional}
              onElegir={elegirProfesional}
              onReintentar={() => elegirServicio(seleccion.servicio)}
              vocabulario={vocabularioDe(negocio)}
            />
          )}

          {paso === 'dia' && (
            <PasoDia
              dias={dias}
              cargando={cargandoDias}
              error={errorDias}
              elegido={seleccion.fecha}
              onElegir={elegirDia}
              onPreseleccionar={preseleccionarDia}
              onReintentar={reintentarDias}
              timezone={zona}
            />
          )}

          {paso === 'horario' && (
            <>
              {avisoOcupado && (
                <div className="aviso" role="alert">
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16h.01" />
                  </svg>
                  <p>{avisoOcupado}</p>
                </div>
              )}
              <PasoHorario
                slots={slots}
                cargando={cargandoSlots}
                error={errorSlots}
                elegido={seleccion.slot}
                onElegir={elegirSlot}
                onReintentar={recargarSlots}
                onOtroDia={() => setPaso('dia')}
              />
            </>
          )}

          {paso === 'pago' && (
            <PasoPago
              opciones={opcionesPago}
              politica={negocio?.senia_politica}
              elegido={seleccion.pago}
              onElegir={elegirPago}
            />
          )}

          {paso === 'datos' && (
            <PasoDatos
              seleccion={seleccion}
              senia={seniaElegida}
              timezone={zona}
              enviando={enviando || yendoAPagar}
              avisoOcupado={avisoOcupado}
              onReservar={reservar}
              vocabulario={vocabularioDe(negocio)}
            />
          )}
        </div>

        <Mapa direccion={negocio?.direccion} nombre={negocio?.nombre} />
      </main>

      <Pie />
    </div>
  );
}
